import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  DragIndicator,
  Reorder,
  LinkedIn as LinkedInIcon
} from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
  colorOptions: [
    { value: "#2f2f2f", label: "Dark Gray" },
    { value: "#2196f3", label: "Blue" },
    { value: "#4caf50", label: "Green" },
    { value: "#ff9800", label: "Orange" },
    { value: "#9c27b0", label: "Purple" },
    { value: "#f44336", label: "Red" }
  ]
};

const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const defaultData = {
  firstName: "Samira",
  lastName: "Hadid",
  currentPosition: "HR Manager",
  phone: "+123-456-7890",
  email: "hello@reallygreatsite.com",
  city: "Any City, Country",
  profile: "I am a Human Resources Professional who's passionate about people-centered development as well as strategic decision-making.",
};

const defaultLeftSectionOrder = [
  'contact',
  'skills',
  'languages',
  'references'
];

const defaultRightSectionOrder = [
  'profile',
  'workExperience',
  'education',
  'certifications',
  'accomplishments',
  'volunteering',
  'interests',
  'additionalInfo'
];

const Resume37 = ({
  color = "#2f2f2f",
  font = "Inter, Poppins, sans-serif",
  fontSize = "12px",
  headingSize = "13px",
  fontStyle = "normal",
  sectionSpacing = 15,
  paragraphSpacing = 20,
  lineSpacing = 16,
  topBottomMargin = 20,
  sideMargins = 20,
  lineWeight = 1,
  pageSize = "A4",
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
  linkedIn = '',
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  
  const sidebarWidth = "30%";
  const contentWidth = "70%";
  
  const data = { 
    ...defaultData, 
    ...formData,
    linkedIn: linkedIn || formData.linkedIn || '',
  };

  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [customSections, setCustomSections] = useState({});
  
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const [sidebarPages, setSidebarPages] = useState([]);
  const rightContentRef = useRef(null);
  const sidebarContentRef = useRef(null);

  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // Global font style that applies to everything
  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
  };

  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    ...additionalStyles
  });

  const getSectionHeaderStyle = () => ({
    ...globalFontStyle,
    fontSize: headingSize,
    fontWeight: "bold",
    marginBottom: `${sectionSpacing}px`,
    color: color
  });

  const getContentSpacingStyle = () => ({
    marginBottom: `${sectionSpacing}px`,
    ...globalFontStyle
  });

  const getParagraphSpacingStyle = () => ({
    marginBottom: `${paragraphSpacing}px`,
    ...globalFontStyle
  });

  const getListItemSpacingStyle = () => ({
    marginBottom: `${paragraphSpacing / 2}px`,
    ...globalFontStyle
  });

  const getLeftSectionHeaderStyle = () => ({
    ...globalFontStyle,
    fontSize: headingSize,
    fontWeight: 700,
    marginBottom: `${sectionSpacing}px`,
    color: "#fff",
    letterSpacing: 1,
    textTransform: "uppercase"
  });

  const SectionBar = ({ title }) => (
    <Box
      style={{
        backgroundColor: color,
        color: "#fff",
        paddingLeft: "20px",
        paddingRight: "20px",
        paddingTop: "5px",
        paddingBottom: "5px",
        borderRadius: "20px",
        fontWeight: 600,
        letterSpacing: "1px",
        marginBottom: `${sectionSpacing}px`,
        display: "inline-block",
        textTransform: "uppercase",
        ...globalFontStyle,
        fontSize: headingSize
      }}
    >
      {title}
    </Box>
  );

  useEffect(() => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) setEducationEntries(JSON.parse(storedEntries));

    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) setSavedSkills(JSON.parse(storedSkills));

    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);

    const storedAdditionalInfo = JSON.parse(localStorage.getItem("additionalInfo")) || [];
    setSavedAdditionalInfo(storedAdditionalInfo);

    const storedLanguages = JSON.parse(localStorage.getItem("languagesList")) || [];
    setSavedLanguages(storedLanguages);

    const storedAccomplishments = JSON.parse(localStorage.getItem("accomplishmentsList")) || [];
    setSavedAccomplishments(storedAccomplishments);

    const storedCertifications = JSON.parse(localStorage.getItem("certificationsList")) || [];
    setSavedCertifications(storedCertifications);

    const storedReferences = JSON.parse(localStorage.getItem("referencesList")) || [];
    setSavedReferences(storedReferences);

    const storedSoftwareSkills = JSON.parse(localStorage.getItem("softwareSkills")) || [];
    setSoftwareSkills(storedSoftwareSkills);

    const storedVolunteering = JSON.parse(localStorage.getItem("volunteering")) || [];
    setSavedVolunteering(storedVolunteering);

    const storedInterests = JSON.parse(localStorage.getItem("interests")) || [];
    setSavedInterests(storedInterests);

    const userId = localStorage.getItem("userId");
    let sectionsData = {};

    if (userId) {
      const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
      currentCustomSections.forEach(sectionName => {
        const userCustomKey = `custom_${userId}_${sectionName}`;
        try {
          const customSectionData = localStorage.getItem(userCustomKey);
          if (customSectionData) {
            const parsedData = JSON.parse(customSectionData);
            const items = parsedData.items || [];
            const validItems = items.filter(item => item && item.trim() !== '');
            if (validItems.length > 0) sectionsData[sectionName] = validItems;
          }
        } catch (error) {
          console.error(`Error loading custom section ${sectionName}:`, error);
        }
      });
    }

    setCustomSections(sectionsData);

    const savedLeftSectionOrder = localStorage.getItem("resume37LeftSectionOrder");
    const savedRightSectionOrder = localStorage.getItem("resume37RightSectionOrder");

    if (savedLeftSectionOrder && savedRightSectionOrder) {
      setLeftSectionOrder(JSON.parse(savedLeftSectionOrder));
      setRightSectionOrder(JSON.parse(savedRightSectionOrder));
    } else {
      const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      setRightSectionOrder([...defaultRightSectionOrder, ...currentCustomSectionIds]);
    }
  }, []);

  useEffect(() => {
    if (leftSectionOrder.length > 0) {
      localStorage.setItem("resume37LeftSectionOrder", JSON.stringify(leftSectionOrder));
    }
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0) {
      localStorage.setItem("resume37RightSectionOrder", JSON.stringify(rightSectionOrder));
    }
  }, [rightSectionOrder]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  const handleLeftDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'left', sectionId: leftSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleRightDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'right', sectionId: rightSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index, side) => {
    e.preventDefault();
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleLeftDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;
    const sourceSide = data.side;
    const sectionId = data.sectionId;

    let newLeftSectionOrder = [...leftSectionOrder];
    let newRightSectionOrder = [...rightSectionOrder];
    
    if (sourceSide === 'left') newLeftSectionOrder.splice(sourceIndex, 1);
    else if (sourceSide === 'right') newRightSectionOrder.splice(sourceIndex, 1);
    
    newLeftSectionOrder.splice(targetIndex, 0, sectionId);
    
    setLeftSectionOrder(newLeftSectionOrder);
    setRightSectionOrder(newRightSectionOrder);
    localStorage.setItem("resume37LeftSectionOrder", JSON.stringify(newLeftSectionOrder));
    localStorage.setItem("resume37RightSectionOrder", JSON.stringify(newRightSectionOrder));
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleRightDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;
    const sourceSide = data.side;
    const sectionId = data.sectionId;

    let newLeftSectionOrder = [...leftSectionOrder];
    let newRightSectionOrder = [...rightSectionOrder];
    
    if (sourceSide === 'left') newLeftSectionOrder.splice(sourceIndex, 1);
    else if (sourceSide === 'right') newRightSectionOrder.splice(sourceIndex, 1);
    
    newRightSectionOrder.splice(targetIndex, 0, sectionId);
    
    setLeftSectionOrder(newLeftSectionOrder);
    setRightSectionOrder(newRightSectionOrder);
    localStorage.setItem("resume37LeftSectionOrder", JSON.stringify(newLeftSectionOrder));
    localStorage.setItem("resume37RightSectionOrder", JSON.stringify(newRightSectionOrder));
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetLeftSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    localStorage.setItem("resume37LeftSectionOrder", JSON.stringify(defaultLeftSectionOrder));
  };

  const resetRightSectionOrder = () => {
    const customSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
    setRightSectionOrder([...defaultRightSectionOrder, ...customSectionIds]);
    localStorage.setItem("resume37RightSectionOrder", JSON.stringify([...defaultRightSectionOrder, ...customSectionIds]));
  };

  const resetAllSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    const customSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
    setRightSectionOrder([...defaultRightSectionOrder, ...customSectionIds]);
  };

  const handleArrangeSectionsClick = () => setArrangeDialogOpen(true);
  const handleArrangeDialogClose = () => setArrangeDialogOpen(false);

  const getOverflowStyle = () => ({
    wordBreak: "break-word",
    overflowWrap: "break-word",
    whiteSpace: "normal"
  });

  const renderSection = (sectionId, isSidebar = false) => {
    const className = isSidebar ? "sidebar-section" : "resume-section";
    const overflowStyles = getOverflowStyle();
    const sectionMarginBottom = isSidebar ? sectionSpacing : sectionSpacing;
    
    switch (sectionId) {
      case 'contact':
        const hasContact = data.phone || data.email || data.city || data.linkedIn;
        const hasPhotoContent = photo;
        
        return hasContact || hasPhotoContent ? (
          <div key="contact" className={className} style={{ marginBottom: `${sectionMarginBottom}px`, ...overflowStyles }}>
            {hasPhotoContent && (
              <Stack alignItems="center" spacing={2} mb={3}>
                <Avatar src={photo} sx={{ width: 130, height: 130, border: "4px solid #fff" }} />
              </Stack>
            )}
            
            {isSidebar ? (
              <Typography style={getLeftSectionHeaderStyle()}>CONTACT</Typography>
            ) : (
              <SectionBar title="Contact" />
            )}
            
            <Box sx={{ width: '100%', ...globalFontStyle, ...overflowStyles }}>
              {data.phone && (
                <Typography sx={{ ...getTextStyle({ mb: 1 }), ...overflowStyles, display: 'flex', alignItems: 'center' }}>
                  <PhoneIcon sx={{ fontSize: 14, mr: 1, flexShrink: 0 }} />
                  <span>{data.phone}</span>
                </Typography>
              )}
              {data.email && (
                <Typography sx={{ ...getTextStyle({ mb: 1 }), ...overflowStyles, display: 'flex', alignItems: 'center' }}>
                  <EmailIcon sx={{ fontSize: 14, mr: 1, flexShrink: 0 }} />
                  <span>{data.email}</span>
                </Typography>
              )}
              {data.city && (
                <Typography sx={{ ...getTextStyle({ mb: 1 }), ...overflowStyles, display: 'flex', alignItems: 'center' }}>
                  <LocationOnIcon sx={{ fontSize: 14, mr: 1, flexShrink: 0 }} />
                  <span>{data.city}</span>
                </Typography>
              )}
              {data.linkedIn && (
                <Typography sx={{ ...getTextStyle({ mb: 1 }), ...overflowStyles, display: 'flex', alignItems: 'center' }}>
                  <LinkedInIcon sx={{ fontSize: 14, mr: 1, flexShrink: 0 }} />
                  <span>{data.linkedIn}</span>
                </Typography>
              )}
            </Box>
            
            {isSidebar && <Divider sx={{ bgcolor: "#aaa", height: `${lineWeight}px`, border: "none", my: `${sectionSpacing}px` }} />}
          </div>
        ) : null;

      case 'skills':
        return (savedSkills.length > 0 || softwareSkills.length > 0) ? (
          <div key="skills" className={className} style={{ marginBottom: `${sectionMarginBottom}px`, ...overflowStyles }}>
            {isSidebar ? <Typography style={getLeftSectionHeaderStyle()}>SKILLS</Typography> : <SectionBar title="Skills" />}
            <ul style={{ paddingLeft: 18, margin: 0, ...globalFontStyle, ...overflowStyles }}>
              {savedSkills.map((skill, index) => (
                <li key={index} style={{ ...getListItemSpacingStyle(), ...overflowStyles }}>
                  {skill.name} {skill.rating ? `(${skill.rating}%)` : ""}
                </li>
              ))}
              {softwareSkills.map((skill, index) => (
                <li key={`software-${index}`} style={{ ...getListItemSpacingStyle(), ...overflowStyles }}>
                  {skill.name} {skill.level ? `(${skill.level}%)` : ""}
                </li>
              ))}
            </ul>
            {isSidebar && <Divider sx={{ bgcolor: "#aaa", height: `${lineWeight}px`, border: "none", my: `${sectionSpacing}px` }} />}
          </div>
        ) : null;

      case 'languages':
        return savedLanguages.length > 0 ? (
          <div key="languages" className={className} style={{ marginBottom: `${sectionMarginBottom}px`, ...overflowStyles }}>
            {isSidebar ? <Typography style={getLeftSectionHeaderStyle()}>LANGUAGES</Typography> : <SectionBar title="Languages" />}
            <Box sx={{ width: '100%', ...globalFontStyle, ...overflowStyles }}>
              {savedLanguages.map((lang, index) => (
                <Typography key={index} sx={getTextStyle({ mb: 1, ...overflowStyles })}>
                  {lang.name} {lang.level ? `– ${lang.level}` : ""}
                </Typography>
              ))}
            </Box>
            {isSidebar && <Divider sx={{ bgcolor: "#aaa", height: `${lineWeight}px`, border: "none", my: `${sectionSpacing}px` }} />}
          </div>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <div key="references" className={className} style={{ marginBottom: `${sectionMarginBottom}px`, ...overflowStyles }}>
            {isSidebar ? <Typography style={getLeftSectionHeaderStyle()}>REFERENCES</Typography> : <SectionBar title="References" />}
            <Box sx={{ width: '100%', ...globalFontStyle, ...overflowStyles }}>
              {savedReferences.map((ref, index) => (
                <Box key={index} sx={{ marginBottom: `${paragraphSpacing}px`, ...overflowStyles }}>
                  <Typography sx={getTextStyle({ fontWeight: 600, ...overflowStyles })}>{ref}</Typography>
                </Box>
              ))}
            </Box>
            {isSidebar && <Divider sx={{ bgcolor: "#aaa", height: `${lineWeight}px`, border: "none", my: `${sectionSpacing}px` }} />}
          </div>
        ) : null;

      case 'profile':
        return (savedSummary || data.profile) ? (
          <div key="profile" className={className} style={{ marginBottom: `${sectionMarginBottom}px`, ...overflowStyles }}>
            {isSidebar ? <Typography style={getLeftSectionHeaderStyle()}>PROFILE</Typography> : <SectionBar title="Profile Summary" />}
            <Typography sx={getTextStyle({ marginBottom: `${paragraphSpacing}px`, ...overflowStyles })}>
              {savedSummary || data.profile}
            </Typography>
            {isSidebar && <Divider sx={{ bgcolor: "#aaa", height: `${lineWeight}px`, border: "none", my: `${sectionSpacing}px` }} />}
          </div>
        ) : null;

      case 'workExperience':
        return workExperiences && workExperiences.length > 0 ? (
          <div key="workExperience" className={className} style={{ marginBottom: `${sectionMarginBottom}px`, ...overflowStyles }}>
            {isSidebar ? <Typography style={getLeftSectionHeaderStyle()}>WORK EXPERIENCE</Typography> : <SectionBar title="Work Experience" />}
            {workExperiences.map((work, i) => (
              <Box key={i} sx={{ marginBottom: `${paragraphSpacing}px`, ...globalFontStyle, ...overflowStyles }}>
                <Typography sx={getTextStyle({ fontWeight: 600, ...overflowStyles })}>
                  {work.jobTitle}
                  {!isSidebar && ` | ${formatDate(work.startMonth, work.startYear)} - ${work.current ? "Present" : formatDate(work.endMonth, work.endYear)}`}
                </Typography>
                <Typography sx={getTextStyle({ fontStyle: 'italic', color: isSidebar ? '#ccc' : '#666', marginBottom: `${paragraphSpacing / 2}px`, ...overflowStyles })}>
                  {isSidebar ? `${formatDate(work.startMonth, work.startYear)} - ${work.current ? "Present" : formatDate(work.endMonth, work.endYear)}` : work.employer}
                </Typography>
                {work.description && !isSidebar && (
                  <Box sx={{ ...globalFontStyle, ...overflowStyles }} dangerouslySetInnerHTML={{ __html: work.description }} />
                )}
              </Box>
            ))}
            {isSidebar && <Divider sx={{ bgcolor: "#aaa", height: `${lineWeight}px`, border: "none", my: `${sectionSpacing}px` }} />}
          </div>
        ) : null;

      case 'education':
        return educationEntries.length > 0 ? (
          <div key="education" className={className} style={{ marginBottom: `${sectionMarginBottom}px`, ...overflowStyles }}>
            {isSidebar ? <Typography style={getLeftSectionHeaderStyle()}>EDUCATION</Typography> : <SectionBar title="Education" />}
            {educationEntries.map((edu) => {
              const institutionName = edu.educationLevel === "school" ? edu.schoolName : edu.collegeName;
              const degreeName = edu.educationLevel === "school" ? edu.standard : edu.degreeName;
              const fieldOfStudy = edu.educationLevel === "school" ? edu.stream : edu.specialization;
              const percentage = edu.percentage;
              const cgpa = edu.cgpa;
              const grade = edu.grade;
              const gradMonth = edu.gradMonth;
              const gradYear = edu.gradYear;
              
              return (
                <Box key={edu.id} sx={{ marginBottom: `${paragraphSpacing}px`, ...globalFontStyle, ...overflowStyles }}>
                  <Typography sx={getTextStyle({ fontWeight: 600, ...overflowStyles })}>
                    {degreeName} {fieldOfStudy && `- ${fieldOfStudy}`}
                  </Typography>
                  <Typography sx={getTextStyle({ marginBottom: `${paragraphSpacing / 2}px`, ...overflowStyles })}>
                    {institutionName} | {formatDate(gradMonth, gradYear)}
                  </Typography>
                  <Typography sx={getTextStyle({ color: isSidebar ? '#ccc' : '#666', ...overflowStyles })}>
                    {percentage && `${percentage}%`}
                    {cgpa && ` | CGPA: ${cgpa}`}
                    {grade && ` | Grade: ${grade}`}
                  </Typography>
                </Box>
              );
            })}
            {isSidebar && <Divider sx={{ bgcolor: "#aaa", height: `${lineWeight}px`, border: "none", my: `${sectionSpacing}px` }} />}
          </div>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <div key="certifications" className={className} style={{ marginBottom: `${sectionMarginBottom}px`, ...overflowStyles }}>
            {isSidebar ? <Typography style={getLeftSectionHeaderStyle()}>CERTIFICATIONS</Typography> : <SectionBar title="Certifications" />}
            {savedCertifications.map((cert, index) => (
              <Box key={index} sx={{ marginBottom: `${paragraphSpacing}px`, ...globalFontStyle, ...overflowStyles }}>
                <Typography sx={getTextStyle({ fontWeight: 600, ...overflowStyles })}>{cert.name}</Typography>
                <Typography sx={getTextStyle({ ...overflowStyles })}>{cert.provider} {cert.year && `(${cert.year})`}</Typography>
              </Box>
            ))}
            {isSidebar && <Divider sx={{ bgcolor: "#aaa", height: `${lineWeight}px`, border: "none", my: `${sectionSpacing}px` }} />}
          </div>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <div key="accomplishments" className={className} style={{ marginBottom: `${sectionMarginBottom}px`, ...overflowStyles }}>
            {isSidebar ? <Typography style={getLeftSectionHeaderStyle()}>ACCOMPLISHMENTS</Typography> : <SectionBar title="Accomplishments" />}
            {savedAccomplishments.map((acc, index) => (
              <Typography key={index} sx={{ ...getListItemSpacingStyle(), ...overflowStyles }}>• {acc}</Typography>
            ))}
            {isSidebar && <Divider sx={{ bgcolor: "#aaa", height: `${lineWeight}px`, border: "none", my: `${sectionSpacing}px` }} />}
          </div>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <div key="volunteering" className={className} style={{ marginBottom: `${sectionMarginBottom}px`, ...overflowStyles }}>
            {isSidebar ? <Typography style={getLeftSectionHeaderStyle()}>VOLUNTEERING</Typography> : <SectionBar title="Volunteering" />}
            {savedVolunteering.map((vol, index) => (
              <Box key={index} sx={{ marginBottom: `${paragraphSpacing}px`, ...globalFontStyle, ...overflowStyles }}>
                <Typography sx={getTextStyle({ fontWeight: 600, ...overflowStyles })}>{vol.subtopic}</Typography>
                <Typography sx={getTextStyle({ fontStyle: 'italic', color: isSidebar ? '#ccc' : '#666', ...overflowStyles })}>
                  {vol.fromDate} - {vol.toDate}
                </Typography>
                <Typography sx={getTextStyle({ ...overflowStyles })}>{vol.content}</Typography>
              </Box>
            ))}
            {isSidebar && <Divider sx={{ bgcolor: "#aaa", height: `${lineWeight}px`, border: "none", my: `${sectionSpacing}px` }} />}
          </div>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <div key="interests" className={className} style={{ marginBottom: `${sectionMarginBottom}px`, ...overflowStyles }}>
            {isSidebar ? <Typography style={getLeftSectionHeaderStyle()}>INTERESTS</Typography> : <SectionBar title="Interests" />}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', ...globalFontStyle, ...overflowStyles }}>
              {savedInterests.map((interest, index) => (
                <Typography key={index} sx={getTextStyle({ mr: 2, ...overflowStyles })}>• {interest}</Typography>
              ))}
            </Box>
            {isSidebar && <Divider sx={{ bgcolor: "#aaa", height: `${lineWeight}px`, border: "none", my: `${sectionSpacing}px` }} />}
          </div>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <div key="additionalInfo" className={className} style={{ marginBottom: `${sectionMarginBottom}px`, ...overflowStyles }}>
            {isSidebar ? <Typography style={getLeftSectionHeaderStyle()}>ADDITIONAL INFORMATION</Typography> : <SectionBar title="Additional Information" />}
            {savedAdditionalInfo.map((info, index) => (
              <Typography key={index} sx={{ ...getListItemSpacingStyle(), ...overflowStyles }}>• {info}</Typography>
            ))}
            {isSidebar && <Divider sx={{ bgcolor: "#aaa", height: `${lineWeight}px`, border: "none", my: `${sectionSpacing}px` }} />}
          </div>
        ) : null;

      default:
        if (sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          const validItems = items.filter(item => item && item.trim() !== '');
          
          if (validItems.length > 0) {
            return (
              <div key={sectionId} className={className} style={{ marginBottom: `${sectionMarginBottom}px`, ...overflowStyles }}>
                {isSidebar ? (
                  <Typography style={getLeftSectionHeaderStyle()}>{customSectionName.replace(/_/g, ' ').toUpperCase()}</Typography>
                ) : (
                  <SectionBar title={customSectionName.replace(/_/g, ' ')} />
                )}
                {validItems.map((item, i) => (
                  <Typography key={i} sx={{ ...getListItemSpacingStyle(), ...overflowStyles }}>• {item}</Typography>
                ))}
                {isSidebar && <Divider sx={{ bgcolor: "#aaa", height: `${lineWeight}px`, border: "none", my: `${sectionSpacing}px` }} />}
              </div>
            );
          }
        }
        return null;
    }
  };

  const renderSidebarSections = () => leftSectionOrder.map(sectionId => renderSection(sectionId, true)).filter(Boolean);
  const renderRightContentSections = () => rightSectionOrder.map(sectionId => renderSection(sectionId, false)).filter(Boolean);

  const getSectionTitle = (sectionId) => {
    const titles = {
      'contact': 'Contact', 'education': 'Education', 'skills': 'Skills', 'languages': 'Languages',
      'profile': 'Profile', 'workExperience': 'Work Experience', 'certifications': 'Certifications',
      'accomplishments': 'Accomplishments', 'volunteering': 'Volunteering', 'interests': 'Interests',
      'additionalInfo': 'Additional Information', 'references': 'References'
    };
    if (sectionId.startsWith('custom_')) return sectionId.replace('custom_', '').replace(/_/g, ' ').toUpperCase();
    return titles[sectionId] || sectionId;
  };

  const renderDragPanel = (side, sections) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', ...globalFontStyle }}>
        <DragIndicator sx={{ mr: 1 }} />
        {side === 'left' ? 'Left Sidebar' : 'Right Content'} Sections ({sections.length} sections)
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
        {sections.map((sectionId, index) => (
          <Paper
            key={sectionId}
            draggable
            onDragStart={(e) => side === 'left' ? handleLeftDragStart(e, index) : handleRightDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index, side)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => side === 'left' ? handleLeftDrop(e, index) : handleRightDrop(e, index)}
            onDragEnd={handleDragEnd}
            sx={{
              p: 2, mb: 1, display: 'flex', alignItems: 'center',
              backgroundColor: dragOverIndex === index && dragOverSide === side ? '#f0f0f0' : 'white',
              cursor: 'grab', '&:active': { cursor: 'grabbing' }, transition: 'all 0.2s ease'
            }}
          >
            <DragIndicator color="action" />
            <Typography sx={{ ml: 1, flexGrow: 1, ...globalFontStyle }}>{getSectionTitle(sectionId)}</Typography>
            <Typography variant="body2" color="textSecondary">{index + 1}</Typography>
          </Paper>
        ))}
      </Box>
      <Button onClick={side === 'left' ? resetLeftSectionOrder : resetRightSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>
        Reset {side === 'left' ? 'Left' : 'Right'} Order
      </Button>
    </Box>
  );

  const paginateSidebarContent = useCallback(() => {
    if (!sidebarContentRef.current) return;
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin);
    const sidebarContent = sidebarContentRef.current;
    const sections = Array.from(sidebarContent.querySelectorAll('.sidebar-section'));
    
    let newSidebarPages = [], currentPageSections = [], currentHeight = 0;
    
    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;
      if (currentHeight + sectionHeight > availableHeight && currentPageSections.length > 0) {
        newSidebarPages.push([...currentPageSections]);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
      if (index === sections.length - 1 && currentPageSections.length > 0) newSidebarPages.push([...currentPageSections]);
    });
    
    setSidebarPages(newSidebarPages.length ? newSidebarPages : [[]]);
  }, [page.height, topBottomMargin, leftSectionOrder, sectionSpacing, paragraphSpacing]);

  const paginateMainContent = useCallback(() => {
    if (!rightContentRef.current) return;
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const headerHeight = 120;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - headerHeight;
    const rightContent = rightContentRef.current;
    const sections = Array.from(rightContent.querySelectorAll('.resume-section'));
    
    let newPages = [], currentPageSections = [], currentHeight = 0;
    
    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;
      if (currentHeight + sectionHeight > availableHeight && currentPageSections.length > 0) {
        newPages.push([...currentPageSections]);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
      if (index === sections.length - 1 && currentPageSections.length > 0) newPages.push([...currentPageSections]);
    });
    
    setPages(newPages.length ? newPages : [[]]);
  }, [page.height, topBottomMargin, rightSectionOrder, sectionSpacing, paragraphSpacing]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      paginateSidebarContent();
      paginateMainContent();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [
    paginateSidebarContent, paginateMainContent, educationEntries, savedSkills, savedSummary,
    workExperiences, savedAdditionalInfo, savedLanguages, savedAccomplishments, savedCertifications,
    savedReferences, softwareSkills, savedVolunteering, savedInterests, customSections,
    leftSectionOrder, rightSectionOrder, sectionSpacing, paragraphSpacing, lineSpacing,
    fontSize, headingSize, font, color, lineWeight, topBottomMargin
  ]);

  const addFontToHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';
    const overflowStyles = "word-break: break-word; overflow-wrap: break-word; white-space: normal;";
    return htmlContent.replace(/<(div|p|span)([^>]*)/g, (match, tagName, attrs) => {
      const styleRegex = /style="([^"]*)"/;
      const styleMatch = attrs.match(styleRegex);
      const defaultStyles = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; ${overflowStyles}`;
      if (styleMatch) {
        const existingStyle = styleMatch[1];
        const needsFontSize = !existingStyle.includes('font-size:');
        const fontSizeStyle = needsFontSize ? `font-size: ${fontSize};` : '';
        const mergedStyle = `style="${defaultStyles} ${fontSizeStyle} ${existingStyle}"`;
        return `<${tagName}${attrs.replace(styleRegex, mergedStyle)}`;
      } else {
        return `<${tagName}${attrs} style="${defaultStyles} font-size: ${fontSize};"`;
      }
    });
  };

  const renderSidebarForMeasurement = () => (
    <Box ref={sidebarContentRef} sx={{ visibility: 'hidden', position: 'absolute', top: -1000, width: sidebarWidth, ...globalFontStyle }}>
      {renderSidebarSections()}
    </Box>
  );

  const renderRightContentForMeasurement = () => (
    <Box ref={rightContentRef} sx={{ visibility: 'hidden', position: 'absolute', top: -1000, width: contentWidth, ...globalFontStyle }}>
      {renderRightContentSections()}
    </Box>
  );

  const totalPages = Math.max(sidebarPages.length, pages.length, 1);

  return (
    <Box sx={{ width: "100%", position: "relative", m: exporting ? 0 : 5, ...globalFontStyle }}>
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, '@media print': { display: 'none' } }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography>Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage === totalPages - 1}>
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, ml: 2 }}>
              <Button onClick={handleArrangeSectionsClick} variant="contained" startIcon={<Reorder />}>Arrange Sections</Button>
              <Button onClick={resetAllSectionOrder} variant="outlined" size="small">Reset All</Button>
            </Box>
          )}
        </Box>
      )}
      
      {renderSidebarForMeasurement()}
      {renderRightContentForMeasurement()}
      
      <Box id="resume-container">
        {Array.from({ length: totalPages }).map((_, pageIndex) => (
          <Box
            key={pageIndex}
            className="resume-page"
            sx={{
              width: page.width,
              minHeight: page.height,
              mx: "auto",
              bgcolor: "#fff",
              boxShadow: pageIndex === currentPage ? 3 : 0,
              marginTop: `${topBottomMargin}px`,
              marginBottom: `${topBottomMargin}px`,
              paddingLeft: `${sideMargins}px`,
              paddingRight: `${sideMargins}px`,
              "@media print": { width: page.width, height: "auto", minHeight: page.height, boxShadow: "none", m: 0, marginTop: `${topBottomMargin}px`, marginBottom: `${topBottomMargin}px` },
              display: exporting ? 'flex' : (pageIndex === currentPage ? 'flex' : 'none'),
              '@media print': { display: 'flex' }
            }}
          >
            <Box sx={{ display: 'flex', width: '100%', flexDirection: 'row' }}>
              <Box sx={{ bgcolor: color, color: "#fff", p: 3, minHeight: page.height, width: sidebarWidth, flexShrink: 0, "@media print": { bgcolor: color, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }, overflow: 'hidden' }}>
                {sidebarPages[pageIndex] ? (
                  <div style={globalFontStyle} dangerouslySetInnerHTML={{ __html: addFontToHtmlContent(sidebarPages[pageIndex].join('')) }} />
                ) : (
                  <Typography sx={{ color: '#fff' }}>Loading...</Typography>
                )}
              </Box>
              
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                {pageIndex === 0 && (
                  <Box sx={{ p: 4, overflow: 'hidden' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, fontFamily: font, fontSize: `calc(${headingSize} * 2.5)`, wordBreak: 'break-word' }}>
                      {data.firstName} {data.lastName}
                    </Typography>
                    <Typography sx={{ letterSpacing: 1, fontSize: fontSize, mb: 3, fontFamily: font, wordBreak: 'break-word' }}>
                      {data.currentPosition}
                    </Typography>
                  </Box>
                )}
                
                {pages[pageIndex] ? (
                  <Box sx={{ p: pageIndex === 0 ? 4 : 0, pt: pageIndex === 0 ? 0 : 'inherit', overflow: 'hidden', width: '100%' }}>
                    <div style={globalFontStyle} dangerouslySetInnerHTML={{ __html: addFontToHtmlContent(pages[pageIndex].join('')) }} />
                  </Box>
                ) : (
                  <Typography>No content</Typography>
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      <Dialog open={arrangeDialogOpen} onClose={handleArrangeDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle><Reorder sx={{ mr: 1 }} /> Arrange Resume Sections</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>Drag and drop sections between columns. Any section can be placed on either side!</Typography>
          <Box sx={{ display: "flex", gap: 3, width: "100%" }}>
            <Box sx={{ flex: 1 }}>{renderDragPanel("left", leftSectionOrder)}</Box>
            <Box sx={{ flex: 1 }}>{renderDragPanel("right", rightSectionOrder)}</Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAllSectionOrder}>Reset All</Button>
          <Button onClick={handleArrangeDialogClose} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume37;